import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';

// Layout Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';

// Pages
import LandingPage from './pages/LandingPage';
import CoursesPage from './pages/CoursesPage';
import CourseDetailPage from './pages/CourseDetailPage';
import StudentDashboard from './pages/StudentDashboard';
import StudentPortal from './pages/StudentPortal';
import AdminDashboard from './pages/AdminDashboard';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ForcePasswordChange from './components/ForcePasswordChange';
import LearnPage from './pages/LearnPage';
import CourseManager from './pages/CourseManager';
import CourseForm from './pages/CourseForm';
import AboutPage from './pages/AboutPage';
import ProfileSettings from './pages/ProfileSettings';
import BookingConsultationPage from './pages/BookingConsultationPage';
import ConsultationManager from './pages/ConsultationManager';
import BookingsList from './pages/BookingsList';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import GDPRPage from './pages/GDPRPage';
import AboutUsPage from './pages/AboutUsPage';
import CareersPage from './pages/CareersPage';
import ContactPage from './pages/ContactPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import BlogManager from './pages/BlogManager';
import BlogForm from './pages/BlogForm';
import LinksManager from './pages/LinksManager';
import ConsultationFeesPage from './pages/ConsultationFeesPage';
import ConsultationServicePage from './pages/ConsultationServicePage';
import ServicesManager from './pages/ServicesManager';
import EventsManager from './pages/EventsManager';
import SectionsManager from './pages/Admin/SectionsManager';
import ScheduleManager from './pages/ScheduleManager';
import StudentProgressManager from './pages/StudentProgressManager';
import MerchandisePage from './pages/MerchandisePage';
import MastermindBrochurePage from './pages/MastermindBrochurePage';
import BaziMasteryPage from './pages/BaziMasteryPage';
import QimenAttunementPage from './pages/QimenAttunementPage';
import FengShuiDeskPage from './pages/FengShuiDeskPage';
import CrimsonHorsePage from './pages/CrimsonHorsePage';
import LifetunesManager from './pages/LifetunesManager';
import BaZiComingSoon from './pages/BaZiComingSoon';
import ResourcesPage from './pages/ResourcesPage';
import QMDJComingSoon from './pages/QMDJComingSoon';
import FaceReadingComingSoon from './pages/FaceReadingComingSoon';
import LifeCodeComingSoon from './pages/LifeCodeComingSoon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Protected Route Component - requires authentication
const ProtectedRoute = ({ children, user, isAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const checkedRef = useRef(false);

  useEffect(() => {
    // Prevent double checking
    if (checkedRef.current) return;
    
    // If user data was passed from AuthCallback or already have user, skip auth check
    if (location.state?.user || user) {
      setIsChecking(false);
      checkedRef.current = true;
      return;
    }

    // If we already confirmed not authenticated, redirect to login
    if (isAuthenticated === false) {
      checkedRef.current = true;
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    // Check auth via backend
    const checkAuth = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (!response.ok) throw new Error('Not authenticated');
        setIsChecking(false);
        checkedRef.current = true;
      } catch (error) {
        console.log('Not authenticated, redirecting to login');
        setIsChecking(false);
        checkedRef.current = true;
        navigate('/login', { state: { from: location.pathname } });
      }
    };
    checkAuth();
  }, [location, navigate, isAuthenticated, user]);

  if (isChecking && !user) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return children;
};

// Admin Route Component - requires admin role
const AdminRoute = ({ children, user, isAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [checkedUser, setCheckedUser] = useState(null);
  const checkedRef = useRef(false);

  useEffect(() => {
    // Prevent double checking
    if (checkedRef.current) return;
    
    // If we have user data, check role immediately
    if (user) {
      if (user.role !== 'admin') {
        navigate('/portal', { replace: true });
      }
      setCheckedUser(user);
      setIsChecking(false);
      checkedRef.current = true;
      return;
    }

    // If we already confirmed not authenticated, redirect to login
    if (isAuthenticated === false) {
      checkedRef.current = true;
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    // Check auth via backend
    const checkAuth = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (!response.ok) throw new Error('Not authenticated');
        const userData = await response.json();
        
        if (userData.role !== 'admin') {
          navigate('/portal', { replace: true });
          return;
        }
        
        setCheckedUser(userData);
        setIsChecking(false);
        checkedRef.current = true;
      } catch (error) {
        console.log('Not authenticated, redirecting to login');
        setIsChecking(false);
        checkedRef.current = true;
        navigate('/login', { state: { from: location.pathname } });
      }
    };
    checkAuth();
  }, [location, navigate, isAuthenticated, user]);

  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-400">Verifying access...</p>
        </div>
      </div>
    );
  }

  return children;
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Main App Content Component
const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, []);

  // Handle user data from login/register or route state
  useEffect(() => {
    if (location.state?.user) {
      const userFromState = location.state.user;
      setUser(() => userFromState);
      setIsAuthenticated(() => true);
      // Clear state to prevent stale data
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setIsAuthenticated(false);
    navigate('/');
  };

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      <ScrollToTop />
      <Navbar user={user} onLogout={handleLogout} />

      {/* Forced password change — shown when user logged in with a temp password */}
      {user && user.must_change_password && (
        <ForcePasswordChange
          user={user}
          onSuccess={() => setUser((prev) => prev ? { ...prev, must_change_password: false } : prev)}
        />
      )}
      
      <main className="flex-1">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/bazi" element={<BaZiComingSoon />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/qmdj" element={<QMDJComingSoon />} />
          <Route path="/face-reading" element={<FaceReadingComingSoon />} />
          <Route path="/lifecode" element={<LifeCodeComingSoon />} />
          <Route path="/lifemastermind2026" element={<MastermindBrochurePage />} />
          <Route path="/BAZIMASTERY" element={<BaziMasteryPage />} />
          <Route path="/QIMENATTUNEMENT" element={<QimenAttunementPage />} />
          <Route path="/FENGSHUIDESKAUDIT" element={<FengShuiDeskPage />} />
          <Route path="/CRIMSONHORSE2026" element={<CrimsonHorsePage />} />
          <Route path="/courses" element={<CoursesPage />} />
          <Route path="/course/:slug" element={<CourseDetailPage user={user} />} />
          <Route path="/checkout/:slug" element={<CheckoutPage user={user} />} />
          <Route path="/booking" element={<BookingConsultationPage user={user} />} />
          
          {/* Company Pages */}
          <Route path="/about-us" element={<AboutUsPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/contact" element={<ContactPage />} />
          
          {/* Legal Pages */}
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/cookies" element={<CookiePolicyPage />} />
          <Route path="/gdpr" element={<GDPRPage />} />
          
          {/* Blog Pages */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          
          {/* Consultation Fees Pages */}
          <Route path="/consultation-fees" element={<ConsultationFeesPage />} />
          <Route path="/consultation-fees/:slug" element={<ConsultationServicePage />} />
          
          {/* Login/Register */}
          <Route 
            path="/login" 
            element={
              user ? <Navigate to="/portal" replace /> : <LoginPage onLogin={handleLogin} />
            } 
          />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Protected Routes - Requires Authentication */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute user={user} isAuthenticated={isAuthenticated}>
                {user?.role === 'admin' ? (
                  <AdminDashboard user={user} />
                ) : (
                  <StudentDashboard user={user} />
                )}
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/profile"
            element={
              <ProtectedRoute user={user} isAuthenticated={isAuthenticated}>
                <ProfileSettings user={user} onUpdateUser={(updatedUser) => setUser(updatedUser)} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/portal"
            element={
              <ProtectedRoute user={user} isAuthenticated={isAuthenticated}>
                <StudentPortal />
              </ProtectedRoute>
            }
          />
          <Route path="/merchandise" element={<MerchandisePage />} />
          <Route
            path="/portal/preview/:studentId"
            element={
              <AdminRoute user={user} isAuthenticated={isAuthenticated}>
                <StudentPortal />
              </AdminRoute>
            }
          />
          
          {/* Admin Routes - Requires Admin Role */}
          <Route
            path="/admin"
            element={
              <AdminRoute user={user} isAuthenticated={isAuthenticated}>
                <AdminDashboard user={user} />
              </AdminRoute>
            }
          />
          
          <Route
            path="/admin/courses"
            element={
              <AdminRoute user={user} isAuthenticated={isAuthenticated}>
                <CourseManager user={user} />
              </AdminRoute>
            }
          />
          
          <Route
            path="/admin/courses/new"
            element={
              <AdminRoute user={user} isAuthenticated={isAuthenticated}>
                <CourseForm user={user} />
              </AdminRoute>
            }
          />
          
          <Route
            path="/admin/courses/:courseId/edit"
            element={
              <AdminRoute user={user} isAuthenticated={isAuthenticated}>
                <CourseForm user={user} />
              </AdminRoute>
            }
          />
          
          <Route
            path="/admin/consultations"
            element={
              <AdminRoute user={user} isAuthenticated={isAuthenticated}>
                <ConsultationManager user={user} />
              </AdminRoute>
            }
          />
          
          <Route
            path="/admin/bookings"
            element={
              <AdminRoute user={user} isAuthenticated={isAuthenticated}>
                <BookingsList user={user} />
              </AdminRoute>
            }
          />
          
          <Route
            path="/admin/blog"
            element={
              <AdminRoute user={user} isAuthenticated={isAuthenticated}>
                <BlogManager user={user} />
              </AdminRoute>
            }
          />
          
          <Route
            path="/admin/blog/new"
            element={
              <AdminRoute user={user} isAuthenticated={isAuthenticated}>
                <BlogForm user={user} />
              </AdminRoute>
            }
          />
          
          <Route
            path="/admin/blog/:postId/edit"
            element={
              <AdminRoute user={user} isAuthenticated={isAuthenticated}>
                <BlogForm user={user} />
              </AdminRoute>
            }
          />
          
          <Route
            path="/admin/links"
            element={
              <AdminRoute user={user} isAuthenticated={isAuthenticated}>
                <LinksManager user={user} />
              </AdminRoute>
            }
          />
          
          <Route
            path="/admin/services"
            element={
              <AdminRoute user={user} isAuthenticated={isAuthenticated}>
                <ServicesManager user={user} />
              </AdminRoute>
            }
          />
          
          <Route
            path="/admin/events"
            element={
              <AdminRoute user={user} isAuthenticated={isAuthenticated}>
                <EventsManager user={user} />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/sections"
            element={
              <AdminRoute user={user} isAuthenticated={isAuthenticated}>
                <SectionsManager />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/schedule"
            element={
              <AdminRoute user={user} isAuthenticated={isAuthenticated}>
                <ScheduleManager user={user} />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/students"
            element={
              <AdminRoute user={user} isAuthenticated={isAuthenticated}>
                <StudentProgressManager user={user} />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/lifetunes"
            element={
              <AdminRoute user={user} isAuthenticated={isAuthenticated}>
                <LifetunesManager user={user} />
              </AdminRoute>
            }
          />
          
          {/* Learning Route - Requires Authentication */}
          <Route
            path="/learn/:slug"
            element={
              <ProtectedRoute user={user} isAuthenticated={isAuthenticated}>
                <LearnPage user={user} />
              </ProtectedRoute>
            }
          />
          
          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      
      <Footer />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
